import React, { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, Calendar, Menu, Mic, Sparkles, MicOff, Coins, Volume2 } from 'lucide-react';
import { useVoiceCopilot } from '@/contexts/VoiceCopilotContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

interface MobileBottomBarProps {
  onToggleMenu: () => void;
}

const MobileBottomBar: React.FC<MobileBottomBarProps> = ({ onToggleMenu }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    isListening,
    isProcessing,
    isSpeaking,
    isSupported,
    transcript,
    feedbackMessage,
    startListening,
    stopListening,
    stopSpeaking,
  } = useVoiceCopilot();

  // Refs para soporte de Modo Walkie-Talkie (Hold-to-Talk)
  const pressStartTimeRef = useRef<number>(0);
  const wasListeningOnDownRef = useRef<boolean>(false);

  // Mostrar oportunamente en modo móvil o PWA
  if (!isMobile) return null;

  const handlePointerDown = () => {
    if (!isSupported || isProcessing) return;
    if (isSpeaking) {
      stopSpeaking();
      return;
    }
    pressStartTimeRef.current = Date.now();
    wasListeningOnDownRef.current = isListening;
    if (!isListening) {
      startListening();
    }
  };

  const handlePointerUp = () => {
    if (!isSupported || isProcessing) return;
    const duration = Date.now() - pressStartTimeRef.current;
    if (duration > 250) {
      // Modo Walkie-Talkie: Mantuvo presionado mientras hablaba, al soltar enviamos la orden
      if (isListening) {
        stopListening();
      }
    } else {
      // Modo Tap Rápido: Si ya estaba escuchando al tocar, detenemos la escucha
      if (wasListeningOnDownRef.current && isListening) {
        stopListening();
      }
    }
  };

  const navItems = [
    {
      label: 'Inicio',
      icon: Home,
      path: '/',
      onClick: () => navigate('/'),
      isActive: location.pathname === '/' || location.pathname === '/dashboard',
    },
    {
      label: 'Ventas',
      icon: Coins,
      path: '/sales',
      onClick: () => navigate('/sales'),
      isActive: location.pathname.startsWith('/sales'),
    },
    // El slot central se reserva para el botón de Voz AI
    {
      label: 'Eventos',
      icon: Calendar,
      path: '/events',
      onClick: () => navigate('/events'),
      isActive: location.pathname.startsWith('/events'),
    },
    {
      label: 'Menú',
      icon: Menu,
      path: '#menu',
      onClick: onToggleMenu,
      isActive: false,
    },
  ];

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
      {/* Indicador flotante de transcripción en vivo o habla en móvil */}
      {isListening && (
        <div className="absolute -top-14 left-4 right-4 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-900/95 text-purple-100 border border-purple-500/50 text-xs font-medium shadow-2xl animate-in fade-in-50 truncate max-w-xs">
            <Sparkles className="h-4 w-4 text-purple-400 animate-spin shrink-0" />
            <span className="truncate">
              {transcript.trim().length > 0 ? `"${transcript}"` : 'Escuchando... (Suelta para procesar)'}
            </span>
          </div>
        </div>
      )}

      {isSpeaking && (
        <div 
          onClick={stopSpeaking}
          className="absolute -top-14 left-4 right-4 flex items-center justify-center cursor-pointer active:scale-95 transition-transform"
        >
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/95 text-emerald-200 border border-emerald-500/50 text-xs font-medium shadow-2xl animate-in fade-in-50 truncate max-w-xs">
            <Volume2 className="h-4 w-4 text-emerald-400 animate-pulse shrink-0" />
            <span className="truncate">
              {feedbackMessage || 'BASS Line respondiendo... (Toca para callar)'}
            </span>
          </div>
        </div>
      )}

      {/* Fondo glassmorphism de la barra inferior */}
      <div className="bg-card/95 backdrop-blur-xl border-t border-border/80 px-3 py-2 shadow-2xl">
        <div className="flex items-center justify-around relative max-w-md mx-auto">
          {/* Primeros dos ítems (Inicio y Clientes) */}
          {navItems.slice(0, 2).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                onClick={item.onClick}
                className={cn(
                  'flex flex-col items-center justify-center w-14 py-1 transition-colors duration-200 rounded-lg',
                  item.isActive
                    ? 'text-purple-600 dark:text-purple-400 font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5',
                    item.isActive ? 'scale-110 transition-transform' : ''
                  )}
                />
                <span className="text-[10px] mt-1 tracking-tight">{item.label}</span>
              </button>
            );
          })}

          {/* ── BOTÓN CENTRAL DE VOZ AI (MODO WALKIE-TALKIE / FEEDBACK) ── */}
          <div className="relative flex flex-col items-center -top-6">
            <button
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none', userSelect: 'none' }}
              disabled={!isSupported || isProcessing}
              aria-label="Botón Walkie-Talkie BASS Line AI"
              className={cn(
                'relative flex items-center justify-center w-16 h-16 rounded-full transition-all duration-300 shadow-2xl select-none',
                !isSupported
                  ? 'bg-muted text-muted-foreground/40 cursor-not-allowed border border-border'
                  : isSpeaking
                  ? 'bg-emerald-600 text-white shadow-emerald-500/50 scale-105 ring-4 ring-emerald-500/40'
                  : isListening
                  ? 'bg-purple-600 text-white shadow-purple-500/60 scale-110 animate-pulse ring-4 ring-purple-500/50'
                  : isProcessing
                  ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-2 border-purple-500/50 animate-pulse'
                  : 'bg-gradient-to-tr from-purple-600 via-pink-600 to-indigo-600 text-white shadow-purple-600/50 hover:scale-105 active:scale-95 border-2 border-purple-400/40'
              )}
            >
              {!isSupported ? (
                <MicOff className="h-7 w-7" />
              ) : isSpeaking ? (
                <Volume2 className="h-8 w-8 animate-pulse text-white" />
              ) : isListening ? (
                <Mic className="h-8 w-8 animate-bounce text-white" />
              ) : isProcessing ? (
                <Sparkles className="h-8 w-8 animate-spin text-purple-600 dark:text-purple-400" />
              ) : (
                <Mic className="h-8 w-8 text-white drop-shadow-md" />
              )}

              {/* Anillo visual de pulso continuo mientras está escuchando */}
              {isListening && (
                <span className="absolute -inset-1 rounded-full border-2 border-purple-400 animate-ping opacity-75 pointer-events-none" />
              )}
            </button>

            <span
              className={cn(
                'text-[10px] font-bold mt-1.5 tracking-tight px-2 py-0.5 rounded-full',
                isSpeaking
                  ? 'bg-emerald-600 text-white animate-pulse'
                  : isListening
                  ? 'bg-purple-600 text-white animate-pulse'
                  : isProcessing
                  ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400'
                  : 'text-foreground/90 bg-muted/60'
              )}
            >
              {isSpeaking ? 'Hablando...' : isListening ? t('copilot.listeningHint', 'Suelta...') : isProcessing ? t('copilot.processing', 'IA...') : t('copilot.voiceAi', 'Voz AI')}
            </span>
          </div>

          {/* Últimos dos ítems (Eventos y Menú) */}
          {navItems.slice(2).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                onClick={item.onClick}
                className={cn(
                  'flex flex-col items-center justify-center w-14 py-1 transition-colors duration-200 rounded-lg',
                  item.isActive
                    ? 'text-purple-600 dark:text-purple-400 font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5',
                    item.isActive ? 'scale-110 transition-transform' : ''
                  )}
                />
                <span className="text-[10px] mt-1 tracking-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileBottomBar;
